import { accountZone } from "../ui/instant.js";

/**
 * FeedRequestLog
 *
 * Every outbound request we made *for this calendar*, newest first: fetching the
 * iCal is the request this tab is about. It is deliberately narrow about that.
 *
 * Two neighbouring ideas belong elsewhere and the copy says so, because mixing
 * them is the obvious mistake: a webhook delivery is the project's Delivery log
 * (this tab never mentions the callback), and a detected change is recorded here
 * only as a marker on the poll that saw it — the change itself lives in the
 * feed's change history and in the delivery log.
 *
 * For a fixed-interval feed each entry is also compared with the plan. The plan
 * is reconstructed from the log itself — the previous poll plus the interval —
 * rather than from the schedule endpoint, because the schedule describes the
 * *next* poll and the past cannot be re-derived from it once the interval has
 * changed or the worker has restarted.
 *
 * Props:
 *   attempts — [{ at, statusCode, notModified, durationMs, succeeded, error,
 *                 changed, eventsChanged, metadataChanged, metadataFields }],
 *              newest first
 *   intervalMinutes — number | null; the fixed interval, when there is one
 *   maxAttempts, maxAgeHours — the retention, reported by the server
 *   now — injectable clock for deterministic labels
 */

function parse(value) {
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : at;
}

function formatTime(value) {
  const at = parse(value);
  if (!at) return "—";
  return at.toLocaleTimeString(undefined, { timeZone: accountZone(), hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function formatDay(value) {
  const at = parse(value);
  if (!at) return "";
  return at.toLocaleDateString(undefined, { timeZone: accountZone(), weekday: "short", day: "numeric", month: "short" });
}

function relative(value, now) {
  const at = parse(value);
  if (!at) return "";
  const seconds = Math.round((at.getTime() - now.getTime()) / 1000);
  if (seconds > -60) return "just now";
  if (seconds > -3600) return `${Math.round(-seconds / 60)} min ago`;
  const hours = Math.floor(-seconds / 3600);
  return `${hours}h ago`;
}

/** One decimal is enough to see a worker that is a little behind. */
function formatDelta(milliseconds) {
  const seconds = milliseconds / 1000;
  const rounded = Math.round(seconds * 10) / 10;
  if (rounded === 0) return "on time";
  return `${rounded > 0 ? "+" : ""}${rounded}s`;
}

/**
 * The delay of each attempt against the plan implied by the one before it.
 * Index 0 is the newest, so its predecessor is index 1.
 */
function delays(attempts, intervalMinutes) {
  if (!intervalMinutes || intervalMinutes <= 0) return [];
  const step = intervalMinutes * 60_000;
  return attempts.map((attempt, index) => {
    const previous = attempts[index + 1];
    if (!previous) return null;
    const actual = parse(attempt.at);
    const before = parse(previous.at);
    if (!actual || !before) return null;
    return actual.getTime() - before.getTime() - step;
  });
}

function statusLabel(attempt) {
  if (attempt.notModified) return "304";
  if (attempt.statusCode) return String(attempt.statusCode);
  return "—";
}

export default function FeedRequestLog({
  attempts = [],
  intervalMinutes = null,
  maxAttempts = 0,
  maxAgeHours = 0,
  now = new Date(),
}) {
  const deltas = delays(attempts, intervalMinutes);
  const failures = attempts.filter((attempt) => !attempt.succeeded).length;

  return (
    <div className="ical-card">
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 4 }}>
        <p style={{ margin: 0, fontWeight: 500, fontSize: "var(--text-base)" }}>
          Last {attempts.length} {attempts.length === 1 ? "request" : "requests"}
        </p>
        {failures ? (
          <span className="ical-badge ical-badge-danger">{failures} failed</span>
        ) : attempts.length ? (
          <span className="ical-badge ical-badge-success">all succeeded</span>
        ) : null}
      </div>

      <p style={{ margin: "0 0 12px", fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
        Every request <em>we</em> made to fetch this calendar, newest first, in your local
        timezone. Webhook deliveries are not here — they are on the project's Delivery log — and a
        change appears below only as a marker on the poll that saw it.
        {" "}
        {maxAttempts ? (
          <>
            Kept for the last {maxAttempts.toLocaleString()} requests or {maxAgeHours} hours,
            whichever comes first — this trail is short-lived by design.
          </>
        ) : (
          <>This trail is short-lived by design.</>
        )}
      </p>

      {attempts.length === 0 ? (
        <p style={{ margin: 0, fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
          No requests recorded yet. The trail fills in as the worker polls; a feed that has not
          been fetched since this deployment started has nothing to show.
        </p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--text-sm)" }}>
          <thead>
            <tr style={{ color: "var(--text-secondary)", fontSize: "var(--text-xs)" }}>
              <th style={{ textAlign: "left", padding: "4px 8px 4px 0", fontWeight: 500 }}>When</th>
              <th style={{ textAlign: "left", padding: "4px 8px", fontWeight: 500 }}>Local time</th>
              <th style={{ textAlign: "left", padding: "4px 8px", fontWeight: 500 }}>Response</th>
              <th style={{ textAlign: "left", padding: "4px 8px", fontWeight: 500 }}>Found</th>
              <th style={{ textAlign: "left", padding: "4px 8px", fontWeight: 500 }}>Took</th>
              {intervalMinutes ? (
                <th style={{ textAlign: "left", padding: "4px 0 4px 8px", fontWeight: 500 }}>Vs plan</th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {attempts.map((attempt, index) => {
              const delta = deltas[index];
              return (
                <tr key={`${attempt.at}-${index}`} style={{ borderTop: "1px solid var(--border-subtle, rgba(0,0,0,0.08))" }}>
                  <td style={{ padding: "6px 8px 6px 0", whiteSpace: "nowrap" }}>
                    <span>{formatDay(attempt.at)}</span>
                    <span style={{ color: "var(--text-muted)" }}> · </span>
                    {/* Its own element so the relative label is readable on
                        its own, by a screen reader and by an assertion. */}
                    <span style={{ color: "var(--text-muted)" }}>{relative(attempt.at, now)}</span>
                  </td>
                  <td style={{ padding: "6px 8px", fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}>
                    {formatTime(attempt.at)}
                  </td>
                  <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                    {attempt.succeeded ? (
                      <span className={`ical-badge ${attempt.notModified ? "ical-badge-neutral" : "ical-badge-success"}`}>
                        {statusLabel(attempt)}
                      </span>
                    ) : (
                      <span className="ical-badge ical-badge-danger" title={attempt.error || undefined}>
                        {attempt.statusCode ? statusLabel(attempt) : "failed"}
                      </span>
                    )}
                    {attempt.notModified ? (
                      <span style={{ color: "var(--text-muted)", fontSize: "var(--text-xs)" }}> not modified</span>
                    ) : null}
                  </td>
                  <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                    {attempt.changed ? (
                      <span className="ical-badge ical-badge-success">
                        {attempt.eventsChanged === 1
                          ? "1 event changed"
                          : `${attempt.eventsChanged} events changed`}
                      </span>
                    ) : attempt.metadataChanged ? (
                      /*
                        The calendar's own metadata moved and no event did — a
                        provider regenerating its export stamp. The server records
                        it and deliberately sends no webhook, so this marker is
                        where the decision can be audited.
                      */
                      <span
                        className="ical-badge ical-badge-neutral"
                        title={
                          attempt.metadataFields.length
                            ? `Calendar metadata changed: ${attempt.metadataFields.join(", ")} — no webhook is sent for this`
                            : "Calendar metadata changed, with no event change — no webhook is sent for this"
                        }
                      >
                        metadata only
                      </span>
                    ) : (
                      <span style={{ color: "var(--text-muted)" }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: "6px 8px", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                    {attempt.durationMs} ms
                  </td>
                  {intervalMinutes ? (
                    <td style={{ padding: "6px 0 6px 8px", whiteSpace: "nowrap" }}>
                      {delta == null ? (
                        <span style={{ color: "var(--text-muted)" }}>—</span>
                      ) : Math.abs(delta) < 5_000 ? (
                        <span style={{ color: "var(--text-secondary)" }}>{formatDelta(delta)}</span>
                      ) : (
                        <span style={{ color: "var(--text-danger)" }}>{formatDelta(delta)}</span>
                      )}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {intervalMinutes ? (
        <p style={{ margin: "12px 0 0", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
          "Vs plan" compares each request with the one before it plus the {intervalMinutes}-minute
          interval. The plan is reconstructed from the trail itself, so a restart or an interval
          change shows up as a jump rather than as a claim about the past.
        </p>
      ) : null}
    </div>
  );
}
